import { Edit2, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui";
import { getCategoryLucideIcon } from "@/components/documents/category-icon-map";
import { useDocumentCategoryLabel } from "@/components/documents/use-document-category-label";
import { useCategoryDocumentCount } from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";

interface Category {
  _id: Id<"documentCategories">;
  name: string;
  slug?: string;
  icon: string;
  isSystem: boolean;
}

interface CategoryListProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}

function CategoryRow({
  category,
  onEdit,
  onDelete,
}: {
  category: Category;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
}) {
  const { count, isLoading } = useCategoryDocumentCount(category._id);
  const getCategoryLabel = useDocumentCategoryLabel();
  const IconComponent = getCategoryLucideIcon(category.icon);
  const displayName = getCategoryLabel({
    name: category.name,
    slug: category.slug,
  });

  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-card-hover transition-colors border-b border-border last:border-0">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {IconComponent && (
          <div className="h-8 w-8 rounded-md bg-muted/30 flex items-center justify-center shrink-0">
            <IconComponent className="h-4 w-4 text-secondary" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-primary truncate">
              {displayName}
            </p>
            {category.isSystem && (
              <Lock className="h-3 w-3 text-muted shrink-0" />
            )}
          </div>
          <p className="text-xs text-secondary">
            {isLoading ? "..." : `${count} document${count === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {!category.isSystem && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            icon={Edit2}
            onClick={() => onEdit(category)}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            onClick={() => onDelete(category)}
          />
        </div>
      )}
    </div>
  );
}

export function CategoryList({
  categories,
  onEdit,
  onDelete,
}: CategoryListProps) {
  return (
    <div className="border border-border rounded-card overflow-hidden">
      {categories.map((category) => (
        <CategoryRow
          key={category._id}
          category={category}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
